import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Check, Mail, Loader2, MessageSquareText, Send, Ban, AlertCircle } from 'lucide-react';
import { atlasApi, AtlasUser, DiscConvite, DiscConviteStatus, linkDiscPublico } from '../shared/atlasApi';

export function dataHora(s?: string | null): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
}

export const STATUS_CONVITE: Record<DiscConviteStatus, { label: string; cls: string }> = {
  pendente: { label: 'Aguardando resposta', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  respondido: { label: 'Respondido', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  expirado: { label: 'Link expirado', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  cancelado: { label: 'Cancelado', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
  substituido: { label: 'Substituído', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
};

export const BadgeConvite: React.FC<{ status: DiscConviteStatus }> = ({ status }) => {
  const s = STATUS_CONVITE[status];
  return <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${s.cls}`}>{s.label}</span>;
};

/** Copia com fallback: navigator.clipboard só existe em https/localhost. */
export async function copiarTexto(texto: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch { /* cai no fallback */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = texto;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch { return false; }
}

function mensagemPronta(c: DiscConvite, link: string): string {
  const primeiro = (c.nome || '').split(' ')[0];
  const nome = primeiro ? primeiro.charAt(0) + primeiro.slice(1).toLowerCase() : '';
  return `Olá${nome ? `, ${nome}` : ''}! Tudo bem? Pedimos que você responda o questionário de perfil `
    + `comportamental (DISC). São 24 perguntas rápidas, leva uns 10 minutos.\n\n${link}\n\n`
    + `O link é pessoal e vale até ${dataHora(c.expira_em)}.`;
}

const BotaoCopiar: React.FC<{ texto: string; label: string; icone: React.ReactNode; destaque?: boolean }> = ({ texto, label, icone, destaque }) => {
  const [copiado, setCopiado] = useState<null | boolean>(null);
  useEffect(() => {
    if (copiado === null) return;
    const t = window.setTimeout(() => setCopiado(null), 2000);
    return () => window.clearTimeout(t);
  }, [copiado]);
  return (
    <button
      onClick={async () => setCopiado(await copiarTexto(texto))}
      className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition ${
        destaque
          ? 'bg-purple-600 hover:bg-purple-700 text-white'
          : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
      }`}
    >
      {copiado ? <Check className="w-4 h-4" /> : icone}
      {copiado === true ? 'Copiado!' : copiado === false ? 'Não deu, copie à mão' : label}
    </button>
  );
};

interface Props {
  user: AtlasUser;
  convite: DiscConvite;
  /** Chamado após enviar e-mail ou cancelar, para a tela recarregar. */
  onAtualizado?: () => void;
  compacto?: boolean;
}

/** Link de um convite pendente: copiar, copiar mensagem pronta, mandar por e-mail, cancelar. */
const LinkConvite: React.FC<Props> = ({ user, convite, onAtualizado, compacto }) => {
  const link = convite.token ? linkDiscPublico(convite.token) : '';
  const [email, setEmail] = useState(convite.email_enviado_para || convite.email_sugerido || '');
  const [enviando, setEnviando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);
  const [mostrarEmail, setMostrarEmail] = useState(!compacto);

  const enviarEmail = useCallback(async () => {
    setEnviando(true);
    setMsg(null);
    try {
      const r = await atlasApi.discEnviarConviteEmail(user, convite.id, email.trim() || undefined);
      setMsg({ ok: true, texto: `E-mail enviado para ${r.email}.` });
      onAtualizado?.();
    } catch (e: any) {
      setMsg({ ok: false, texto: e?.message || 'Falha ao enviar o e-mail.' });
    } finally {
      setEnviando(false);
    }
  }, [user, convite.id, email, onAtualizado]);

  const cancelar = async () => {
    if (!window.confirm(`Cancelar o link de ${convite.nome || 'este colaborador'}? Ele deixa de funcionar na hora.`)) return;
    setCancelando(true);
    try {
      await atlasApi.discCancelarConvite(user, convite.id);
      onAtualizado?.();
    } catch (e: any) {
      setMsg({ ok: false, texto: e?.message || 'Falha ao cancelar.' });
      setCancelando(false);
    }
  };

  if (!link) return null;

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          readOnly
          value={link}
          onFocus={e => e.currentTarget.select()}
          className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-gray-50 font-mono"
        />
        <div className="flex gap-2 flex-wrap">
          <BotaoCopiar texto={link} label="Copiar link" icone={<Copy className="w-4 h-4" />} destaque />
          <BotaoCopiar texto={mensagemPronta(convite, link)} label="Copiar mensagem" icone={<MessageSquareText className="w-4 h-4" />} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
        <span>Vale até <strong className="text-gray-700">{dataHora(convite.expira_em)}</strong></span>
        {convite.acessado_em && <span>Aberto em {dataHora(convite.acessado_em)}</span>}
        {convite.email_enviado_em && <span>E-mail enviado para {convite.email_enviado_para} em {dataHora(convite.email_enviado_em)}</span>}
        {!mostrarEmail && (
          <button onClick={() => setMostrarEmail(true)} className="text-purple-600 hover:underline flex items-center gap-1">
            <Mail className="w-3.5 h-3.5" /> Enviar por e-mail
          </button>
        )}
        <button onClick={cancelar} disabled={cancelando} className="text-gray-400 hover:text-rose-600 flex items-center gap-1 disabled:opacity-50">
          <Ban className="w-3.5 h-3.5" /> Cancelar link
        </button>
      </div>

      {mostrarEmail && (
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="e-mail do colaborador"
            className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
          />
          <button
            onClick={enviarEmail}
            disabled={enviando || !email.trim()}
            className="px-3 py-2 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
          >
            {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Enviar por e-mail
          </button>
        </div>
      )}

      {msg && (
        <div className={`flex items-center gap-2 text-xs ${msg.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
          {msg.ok ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />} {msg.texto}
        </div>
      )}
    </div>
  );
};

export default LinkConvite;
